import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  FaCheckCircle, FaArrowRight, FaIndustry, FaGlobe,
  FaQuoteLeft, FaCogs, FaShippingFast, FaAward,
  FaStar, FaLeaf, FaBolt, FaShieldAlt, FaUserTie,
  FaChartLine, FaHandshake, FaTrophy, FaPhoneAlt
} from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import { getServices, getTestimonials, getCompanyInfo } from '../firebase/db';
import './Home.css';

const fadeInUp = {
  hidden: { opacity: 0, y: 50 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.65, ease: 'easeOut' } }
};

const fadeInLeft = {
  hidden: { opacity: 0, x: -60 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.7, ease: 'easeOut' } }
};

const fadeInRight = {
  hidden: { opacity: 0, x: 60 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.7, ease: 'easeOut' } }
};

const Home = () => {
  const { t, i18n } = useTranslation();
  const isPostposition = (i18n?.language || '').startsWith('mr') || (i18n?.language || '').startsWith('kn');
  const [services, setServices] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [companyPhone, setCompanyPhone] = useState('8956403388');

  const heroSlides = [
    {
      image: "/Images/image1.jpg",
      badge1Icon: <FaTrophy />,
      badge1Title: t('heroBadgeIsoTitle'),
      badge1Desc: t('heroBadgeIsoDesc'),
      badge2Icon: <FaGlobe />,
      badge2Title: t('heroBadgeNationsTitle'),
      badge2Desc: t('heroBadgeNationsDesc')
    },
    {
      image: "/Images/image2.jpg",
      badge1Icon: <FaLeaf />,
      badge1Title: t('heroBadgeYieldTitle'),
      badge1Desc: t('heroBadgeYieldDesc'),
      badge2Icon: <FaCheckCircle />,
      badge2Title: t('heroBadgeZeroMaintTitle'),
      badge2Desc: t('heroBadgeZeroMaintDesc')
    },
    {
      image: "/Images/image3.jpg",
      badge1Icon: <FaShippingFast />,
      badge1Title: t('heroBadgeFastDeliveryTitle'),
      badge1Desc: t('heroBadgeFastDeliveryDesc'),
      badge2Icon: <FaHandshake />,
      badge2Title: t('heroBadgeSupportTitle'),
      badge2Desc: t('heroBadgeSupportDesc')
    }
  ];

  const getServiceTitle = (s) => {
    const lower = (s.title || '').toLowerCase();
    if (lower.includes('conveyor') || lower.includes('material')) return t('svcConveyorTitle');
    if (lower.includes('export') || lower.includes('logistic')) return t('svcExportTitle');
    if (lower.includes('consult') || lower.includes('project')) return t('svcConsultTitle');
    if (lower.includes('install')) return t('svcInstallTitle');
    if (lower.includes('maint') || lower.includes('after-sales') || lower.includes('after sales')) return t('svcMaintTitle');
    if (lower.includes('part') || lower.includes('spare')) return t('svcPartsTitle');
    if (lower.includes('train')) return t('svcTrainingTitle');
    if (lower.includes('custom')) return t('svcCustomTitle');
    if (lower.includes('support') || lower.includes('24/7')) return t('svcSupportTitle');
    return t(s.title, s.title);
  };

  const getServiceDesc = (s) => {
    const lower = (s.title || '').toLowerCase();
    if (lower.includes('conveyor') || lower.includes('material')) return t('svcConveyorDesc');
    if (lower.includes('export') || lower.includes('logistic')) return t('svcExportDesc');
    if (lower.includes('consult') || lower.includes('project')) return t('svcConsultDesc');
    if (lower.includes('install')) return t('svcInstallDesc');
    if (lower.includes('maint') || lower.includes('after-sales') || lower.includes('after sales')) return t('svcMaintDesc');
    if (lower.includes('part') || lower.includes('spare')) return t('svcPartsDesc');
    if (lower.includes('train')) return t('svcTrainingDesc');
    if (lower.includes('custom')) return t('svcCustomDesc');
    if (lower.includes('support') || lower.includes('24/7')) return t('svcSupportDesc');
    return t(s.description, s.description);
  };

  const fallbackHomeServices = [
    { id: '1', title: t('svcInstallTitle'), description: t('svcInstallDesc') },
    { id: '2', title: t('svcMaintTitle'), description: t('svcMaintDesc') },
    { id: '3', title: t('svcPartsTitle'), description: t('svcPartsDesc') },
  ];
  const displayServices = services && services.length > 0 ? services : fallbackHomeServices;

  useEffect(() => {
    document.title = 'Digvijay Industries | Agricultural Machinery Manufacturers';
    const fetchData = async () => {
      const servs = await getServices();
      const tests = await getTestimonials();
      const cInfo = await getCompanyInfo();
      setServices(servs.slice(0, 3));
      setTestimonials(tests);
      if (cInfo && cInfo.phone) {
        const cleaned = cInfo.phone
          .split(',')
          .map(p => p.trim())
          .filter(p => !p.includes('2644484') && !p.includes('0233'))[0];
        if (cleaned) setCompanyPhone(cleaned);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
    }, 5500);
    return () => clearInterval(timer);
  }, [heroSlides.length]);

  const heroTitles = [
    { title1: t('heroTitle1_1'), title2: t('heroTitle1_2'), desc: t('heroDesc1') },
    { title1: t('heroTitle2_1'), title2: t('heroTitle2_2'), desc: t('heroDesc2') },
    { title1: t('heroTitle3_1'), title2: t('heroTitle3_2'), desc: t('heroDesc3') }
  ];

  const STATS = [
    { number: '35+', label: t('stat1Label'), icon: <FaTrophy /> },
    { number: '5000+', label: t('stat2Label'), icon: <FaIndustry /> },
    { number: '3+', label: t('stat3Label'), icon: <FaGlobe /> },
    { number: '24/7', label: t('stat4Label'), icon: <FaHandshake /> },
  ];

  return (
    <div className="home-page">

      {/* =========== HERO BANNER =========== */}
      <section className="hero">
        <div className="hero-particles">
          {[...Array(6)].map((_, i) => (
            <motion.div
              key={i}
              className={`particle particle-${i + 1}`}
              animate={{
                y: [0, -40, 0],
                x: [0, i % 2 === 0 ? 20 : -20, 0],
                rotate: [0, i % 2 === 0 ? 90 : -90, 0],
                opacity: [0.3, 0.8, 0.3]
              }}
              transition={{ duration: 5 + i, repeat: Infinity, ease: 'easeInOut', delay: i * 0.8 }}
            />
          ))}
        </div>

        <div className="container hero-inner" style={{ position: 'relative' }}>
          <AnimatePresence mode="wait">
            <motion.div
              key={`text-${currentSlide}`}
              className="hero-text"
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 30 }}
              transition={{ duration: 0.5, ease: 'easeOut' }}
              style={{ textAlign: 'left' }}
            >
              <h1 className="hero-title">
                {heroTitles[currentSlide].title1} <span className="hero-highlight">{heroTitles[currentSlide].title2}</span>
              </h1>

              <p className="hero-desc">
                {heroTitles[currentSlide].desc}
              </p>

              <div className="hero-cta">
                <Link to="/products" className="btn-hero-primary">
                  {t('btnExploreProducts')} <FaArrowRight />
                </Link>
                <Link to="/contact" className="btn-hero-outline">
                  {t('btnContactSales')}
                </Link>
              </div>
            </motion.div>
          </AnimatePresence>

          <AnimatePresence mode="wait">
            <motion.div
              key={`img-${currentSlide}`}
              className="hero-visual"
              initial={{ opacity: 0, scale: 0.95, rotateY: 10 }}
              animate={{ opacity: 1, scale: 1, rotateY: 0 }}
              exit={{ opacity: 0, scale: 1.05, rotateY: -10 }}
              transition={{ duration: 0.6, ease: 'easeOut' }}
            >
              <div className="hero-img-card">
                <img
                  src={heroSlides[currentSlide].image}
                  alt={heroTitles[currentSlide].title2}
                  className="hero-img"
                />

                <div className="hero-img-overlay" />

                <motion.div
                  className="float-badge left"
                  animate={{ y: [0, -8, 0] }}
                  transition={{ duration: 3, repeat: Infinity }}
                >
                  {heroSlides[currentSlide].badge1Icon} <div><strong>{heroSlides[currentSlide].badge1Title}</strong><span>{heroSlides[currentSlide].badge1Desc}</span></div>
                </motion.div>

                <motion.div
                  className="float-badge right"
                  animate={{ y: [0, 8, 0] }}
                  transition={{ duration: 3.5, repeat: Infinity }}
                >
                  {heroSlides[currentSlide].badge2Icon} <div><strong>{heroSlides[currentSlide].badge2Title}</strong><span>{heroSlides[currentSlide].badge2Desc}</span></div>
                </motion.div>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>

        <div className="hero-slider-controls">
          {heroSlides.map((_, idx) => (
            <div
              key={idx}
              className={`slider-dot ${idx === currentSlide ? 'active' : ''}`}
              onClick={() => setCurrentSlide(idx)}
            />
          ))}
        </div>

        <motion.div
          className="scroll-indicator"
          animate={{ y: [0, 12, 0] }}
          transition={{ duration: 1.5, repeat: Infinity }}
        >
          <div className="scroll-dot" />
        </motion.div>
      </section>

      {/* =========== MARQUEE =========== */}
      <div className="marquee-bar">
        <div className="marquee-track">
          {[...Array(2)].map((_, j) => (
            <div key={j} className="marquee-items">
              <span><FaLeaf /> {t('mq1')}</span>
              <span><FaBolt /> {t('mq2')}</span>
             
              <span><FaGlobe /> {t('mq4')}</span>
              <span><FaAward /> {t('mq5')}</span>
              <span><FaChartLine /> {t('mq6')}</span>
            </div>
          ))}
        </div>
      </div>

      {/* =========== STATS BANNER =========== */}
      <section className="stats-banner">
        <div className="home-container">
          <div className="stats-row">
            {STATS.map((s, i) => (
              <motion.div
                key={i}
                className="stat-item"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.12 }}
                whileHover={{ scale: 1.05 }}
              >
                <div className="stat-icon-wrap">{s.icon}</div>
                <div className="stat-num">{s.number}</div>
                <div className="stat-label">{s.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* =========== COMPANY INTRO =========== */}
      <section className="section bg-light">
        <div className="home-container">
          <div className="intro-grid">
            <motion.div
              className="intro-img-side"
              variants={fadeInLeft}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
            >
              <div className="intro-img-stack">
                <div className="intro-video-wrapper">
                  <iframe
                    src="https://www.youtube.com/embed/QJA8t8otoJs"
                    title="Digvijay Industries Machine Video"
                    className="intro-video-iframe"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  ></iframe>
                </div>
                <div className="intro-img-badge-card">
                  <FaTrophy className="badge-icon" />
                  <div>
                    <strong>{t('introBadgeSince')}</strong>
                    <span>{t('introBadgeLegacy')}</span>
                  </div>
                </div>
              </div>
              <div className="intro-video-caption">
                {t('videoCaption')}
              </div>
            </motion.div>

            <motion.div
              className="intro-text-side"
              variants={fadeInRight}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
            >
              <h2 className="section-title left">
                {isPostposition ? (
                  <>
                    <span className="highlight-text">Digvijay Industries</span> {t('introTitle')}
                  </>
                ) : (
                  <>
                    {t('introTitle')} <span className="highlight-text">Digvijay Industries</span>
                  </>
                )}
              </h2>
              <p className="intro-lead">
                {t('introLead')}
              </p>
              <p>
                {t('introDesc')}
              </p>
              <ul className="feature-list">
                <li><FaCheckCircle /> {t('feature1')}</li>
                <li><FaCheckCircle /> {t('feature2')}</li>
                <li><FaCheckCircle /> {t('feature3')}</li>
                <li><FaCheckCircle /> {t('feature4')}</li>
              </ul>
              <Link to="/about" className="btn-cta">
                {t('btnExploreStory')} <FaArrowRight />
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* =========== DIGVIJAY PROMISES =========== */}
      <section className="section bg-light" style={{ paddingTop: '30px' }}>
        <div className="home-container">
          <motion.div variants={fadeInUp} initial="hidden" whileInView="visible" viewport={{ once: true }} style={{ marginBottom: 40, textAlign: 'center', width: '100%' }}>
            <h2 className="section-title" style={{ textAlign: 'center' }}>{t('commitTitle')} <span className="highlight-text">{t('commitTitleHighlight')}</span></h2>
          </motion.div>
          <div className="why-grid">
            {[
              { icon: <FaCheckCircle />, title: t('why1Title'), desc: t('why1Desc'), color: '#04416d' },
              { icon: <FaBolt />, title: t('why2Title'), desc: t('why2Desc'), color: '#e33355' },
              { icon: <FaHandshake />, title: t('why3Title'), desc: t('why3Desc'), color: '#0b5c96' },
            ].map((w, i) => (
              <motion.div
                key={i}
                className="why-card"
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                whileHover={{ y: -8, boxShadow: `0 20px 40px rgba(0,0,0,0.1)` }}
                style={{ '--card-accent': w.color }}
              >
                <div className="why-icon" style={{ background: `${w.color}15`, color: w.color }}>{w.icon}</div>
                <h3>{w.title}</h3>
                <p>{w.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* =========== PROCESS / HOW WE WORK =========== */}
      <section className="section process-section">
        <div className="process-bg-glow" />
        <div className="home-container" style={{ position: 'relative' }}>
          <motion.div variants={fadeInUp} initial="hidden" whileInView="visible" viewport={{ once: true }} style={{ textAlign: 'center', marginBottom: 40, width: '100%' }}>
            <h2 className="section-title white" style={{ textAlign: 'center' }}>{t('processTitle')} <span className="highlight-text-light">{t('processTitleHighlight')}</span></h2>
          </motion.div>
          <div className="process-grid">
            {[
              { n: '01', icon: <FaUserTie />, title: t('step1Title'), desc: t('step1Desc') },
              { n: '02', icon: <FaCogs />, title: t('step2Title'), desc: t('step2Desc') },
              { n: '03', icon: <FaIndustry />, title: t('step3Title'), desc: t('step3Desc') },
              { n: '04', icon: <FaShippingFast />, title: t('step4Title'), desc: t('step4Desc') },
            ].map((step, i) => (
              <motion.div
                key={i}
                className="process-card"
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.15 }}
                whileHover={{ scale: 1.04, boxShadow: '0 20px 50px rgba(0,0,0,0.3)' }}
              >
                <div className="process-card-header">
                  <div className="process-icon">{step.icon}</div>
                  <div className="process-num">{step.n}</div>
                </div>
                <h3>{step.title}</h3>
                <p>{step.desc}</p>
                {i < 3 && <div className="process-connector" />}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* =========== SERVICES HIGHLIGHT =========== */}
      <section className="section bg-light">
        <div className="home-container">
          <motion.div
            variants={fadeInUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            style={{ textAlign: 'center', marginBottom: 40, width: '100%' }}
          >
            <h2 className="section-title" style={{ textAlign: 'center' }}>{t('servicesTitle')} <span className="highlight-text">{t('servicesTitleHighlight')}</span></h2>
          </motion.div>
          <div className="services-highlight-wrapper">

            <div className="services-highlight-grid">
              {displayServices.map((s, i) => (
                <motion.div
                  key={s.id}
                  className="service-highlight-card"
                  initial={{ opacity: 0, x: i % 2 === 0 ? -40 : 40 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: i * 0.15 }}
                  whileHover={{ y: -6 }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '15px', width: '100%' }}>
                    <div className="svc-icon-wrap" style={{ margin: 0 }}>
                      <FaCogs />
                    </div>
                    <h3 style={{ margin: 0, textAlign: 'left', flex: 1 }}>{getServiceTitle(s)}</h3>
                  </div>
                  <p style={{ textAlign: 'left', width: '100%', margin: 0 }}>{getServiceDesc(s)}</p>
                </motion.div>
              ))}
            </div>

            <div className="services-btn">
              <Link to="/services" className="btn-cta">
                {t('btnAllServices')} <FaArrowRight />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* =========== TESTIMONIALS =========== */}
      <section className="section testimonials-section">
        <div className="testi-bg-glow" />
        <div className="home-container" style={{ position: 'relative' }}>
          <motion.div variants={fadeInUp} initial="hidden" whileInView="visible" viewport={{ once: true }} style={{ textAlign: 'center', marginBottom: 40, width: '100%' }}>
            <h2 className="section-title white" style={{ textAlign: 'center' }}>{t('testiTitle')} <span className="highlight-text-light">{t('testiTitleHighlight')}</span></h2>
          </motion.div>
          <div className="testimonials-marquee-wrapper">
            <div className="testimonials-marquee-track">
              {testimonials.map((tItem, i) => (
                <div key={i} className="testi-card">
                  <div className="testi-stars">
                    {[...Array(tItem.rating || 5)].map((_, s) => <FaStar key={s} />)}
                  </div>
                  <FaQuoteLeft className="testi-quote" />
                  <p>{tItem.text}</p>
                  <div className="testi-author">
                    <div className="testi-avatar">{tItem.name ? tItem.name[0] : 'U'}</div>
                    <div>
                      <strong>{tItem.name}</strong>
                      <span>{tItem.role}</span>
                    </div>
                  </div>
                </div>
              ))}
              {testimonials.map((tItem, i) => (
                <div key={i + 5} className="testi-card">
                  <div className="testi-stars">
                    {[...Array(tItem.rating || 5)].map((_, s) => <FaStar key={s} />)}
                  </div>
                  <FaQuoteLeft className="testi-quote" />
                  <p>{tItem.text}</p>
                  <div className="testi-author">
                    <div className="testi-avatar">{tItem.name ? tItem.name[0] : 'U'}</div>
                    <div>
                      <strong>{tItem.name}</strong>
                      <span>{tItem.role}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* =========== WHY CHOOSE US =========== */}
      <section className="section bg-light">
        <div className="home-container">
          <motion.div variants={fadeInUp} initial="hidden" whileInView="visible" viewport={{ once: true }} style={{ textAlign: 'center', marginBottom: 40, width: '100%' }}>
            <h2 className="section-title" style={{ textAlign: 'center' }}>{t('whyChooseTitle')} <span className="highlight-text">{t('whyChooseTitleHighlight')}</span></h2>
          </motion.div>
          <div className="why-grid">
            {[
              { icon: <FaAward />, title: t('wc1Title'), desc: t('wc1Desc'), color: '#04416d' },
              { icon: <FaShieldAlt />, title: t('wc2Title'), desc: t('wc2Desc'), color: '#e33355' },
              { icon: <FaGlobe />, title: t('wc3Title'), desc: t('wc3Desc'), color: '#0b5c96' },
              { icon: <FaUserTie />, title: t('wc4Title'), desc: t('wc4Desc'), color: '#d4294a' },
              { icon: <FaCogs />, title: t('wc5Title'), desc: t('wc5Desc'), color: '#022a47' },
              { icon: <FaChartLine />, title: t('wc6Title'), desc: t('wc6Desc'), color: '#e33355' },
            ].map((w, i) => (
              <motion.div
                key={i}
                className="why-card"
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                whileHover={{ y: -8, boxShadow: `0 20px 40px rgba(0,0,0,0.1)` }}
                style={{ '--card-accent': w.color }}
              >
                <div className="why-icon" style={{ background: `${w.color}15`, color: w.color }}>{w.icon}</div>
                <h3>{w.title}</h3>
                <p>{w.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* =========== CTA BANNER =========== */}
      <section className="cta-section">
        <div className="cta-glow" />
        <div className="nav-container cta-inner">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            style={{ textAlign: 'center', width: '100%' }}
          >
            <h2>{t('ctaTitle')}</h2>
            <p>{t('ctaDesc')}</p>
            <div className="cta-btns">
              <Link to="/contact" className="btn-hero-primary">{t('btnCallExperts')}</Link>
              <a href={`tel:${companyPhone.replace(/[^0-9+]/g, '')}`} className="btn-hero-outline white">
                <FaPhoneAlt /> +91 {companyPhone}
              </a>
            </div>
          </motion.div>
        </div>
      </section>

    </div>
  );
};

export default Home;
